import {Box, Flex, useTheme} from '@chakra-ui/react';
import React, {useCallback, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {TextG12, TextG14, TextA18} from '../typography';
import styled from 'styled-components';
import dayjs from 'dayjs';
import calendar from 'dayjs/plugin/calendar';
import relativeTime from 'dayjs/plugin/relativeTime';
import {motion} from 'framer-motion';
import Logo from '../../assets/logos/logo_120x120.png';
import {useMarkMessageAsRead} from '../../hooks/useMessages';
import Icon from '../Icon';
import {UserMessage} from '../../types/message';
import BottomModalSheet from '../Modals/BottomModalSheet';
import {useNavigate} from 'react-router-dom';

dayjs.extend(relativeTime);
dayjs.extend(calendar);

const resolveMessageImage = (imageUrl?: string | null): string => {
  return typeof imageUrl === 'string' && imageUrl.length > 0 ? imageUrl : Logo;
}

const stripProtocol = (url: string): string => url.replace(/^https?:\/\//, '');

const URL_REGEX = /(https?:\/\/[^\s]+)/g;

/** Navigate to a URL — uses react-router for same-origin, opens new tab for external */
const navigateToUrl = (url: string, navigate: ReturnType<typeof useNavigate>) => {
  if (url.startsWith(globalThis.location.origin)) {
    const parsed = new URL(url);
    navigate(parsed.pathname + parsed.search + parsed.hash);
  } else {
    globalThis.open(url, '_blank', 'noopener,noreferrer');
  }
};

const BodyWithLinks = ({text, onLinkClick}: {text: string; onLinkClick: (url: string) => void}) => {
  const theme = useTheme();
  const parts = text.split(URL_REGEX);

  return (
    <>
      {parts.map((part, i) =>
        URL_REGEX.test(part) ? (
          <Box
            key={i}
            as="span"
            color={theme.colors.blue}
            cursor="pointer"
            sx={{textDecoration: 'underline'}}
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              onLinkClick(part);
            }}
          >
            {stripProtocol(part)}
          </Box>
        ) : (
          <React.Fragment key={i}>{part}</React.Fragment>
        )
      )}
    </>
  );
};

const BodyText = styled(TextG12 as any)`
  word-break: break-word;
  white-space: pre-line;
  && {
    margin: 0;
    margin-top: 2px;
    font-size: 14px;
    line-height: 22px;
  }
`;

const MessageCard = ({uuid, type, title, body, link, sent_at, read_at, image_url}: UserMessage) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isUnread = !read_at;
  const markAsRead = useMarkMessageAsRead();
  const [showMessageSheet, setShowMessageSheet] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const [collapsedHeight, setCollapsedHeight] = useState<number | undefined>();
  const bodyRef = useRef<HTMLDivElement>(null);

  const formattedTimestamp = useMemo(() => {
    const sentAtDate = dayjs(sent_at);
    if (!sentAtDate.isValid()) return '';
    const diffHours = dayjs().diff(sentAtDate, 'hour');
    if (diffHours < 1) return sentAtDate.fromNow();
    const isCurrentYear = sentAtDate.year() === dayjs().year();
    return sentAtDate.calendar(null, {
      sameDay: 'h:mm A',
      lastDay: '[Yesterday]',
      lastWeek: 'MMM D',
      sameElse: isCurrentYear ? 'MMM D' : "MMM D, YYYY"
    });
  }, [sent_at]);

  const messageImage = resolveMessageImage(image_url);
  const isSms = type === 'sms_message';
  const displayTitle = title || null;
  const displayLink = useMemo(() => link ? stripProtocol(link) : null, [link]);

  useLayoutEffect(() => {
    const el = bodyRef.current;
    if (el && !isExpanded) {
      setIsTruncated(el.scrollHeight > el.clientHeight + 1);
      setCollapsedHeight(el.clientHeight);
    }
  }, [body, isExpanded]);

  /** Marks as read (if unread) and navigates to the given URL. Used by inline link clicks. */
  const handleNavigate = useCallback((url: string) => {
    if (isUnread) markAsRead.mutate(uuid);
    navigateToUrl(url, navigate);
  }, [isUnread, markAsRead, uuid, navigate]);

  /** Card-level click: mark as read, expand if truncated, then navigate or show sheet. */
  const handleClick = useCallback(() => {
    if (isUnread) markAsRead.mutate(uuid);

    if (isTruncated && !isExpanded) {
      setIsExpanded(true);
      return;
    }

    if (link) {
      navigateToUrl(link, navigate);
    } else {
      setShowMessageSheet(true);
    }
  }, [isUnread, markAsRead, uuid, isTruncated, isExpanded, link, navigate]);

  return (
    <Box minW="100%" minH="100%">
      <Box
        pl={2}
        pr={4}
        py={3}
        borderRadius="0px"
        cursor="pointer"
        transition="background-color 0.15s ease"
        mb={0}
        bg={isUnread ? theme.colors.blueShade3 : 'transparent'}
        onClick={handleClick}
        role="article"
        aria-label={`${isUnread ? 'Unread ' : ''}message${displayTitle ? `: ${displayTitle}` : ''}`}
        tabIndex={0}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClick();
          }
        }}
      >
        <Flex gap="12px" w="100%" align="flex-start">
          <Flex flex="1 1 30%" maxW="fit-content" mt="4px">
            <Box
              as="img"
              src={messageImage}
              alt="Message"
              borderRadius="full"
              h="40px"
              w="40px"
              objectFit="cover"
              border="1px solid rgba(0, 0, 0, 0.05)"
              bg="#f5f5f5"
            />
          </Flex>
          <Flex flex="1 1 100%" flexDirection="column" gap="2px">
            {displayTitle && (
              <TextG12 fontSize="14px" lineHeight="20px" mt="0" fontWeight="500">
                {displayTitle}
              </TextG12>
            )}
            <motion.div
              initial={false}
              animate={{height: isExpanded ? 'auto' : collapsedHeight}}
              transition={{duration: 0.25, ease: 'easeOut'}}
              style={{overflow: 'hidden'}}
            >
              <Box
                ref={bodyRef}
                sx={{
                  ...(!isExpanded && {
                    display: '-webkit-box',
                    WebkitLineClamp: isSms ? 3 : 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }),
                }}
              >
                <BodyText>
                  {isExpanded && isSms ? (
                    <BodyWithLinks text={body} onLinkClick={handleNavigate} />
                  ) : (
                    body
                  )}
                </BodyText>
              </Box>
              {isExpanded && !isSms && link && displayLink && (
                <BodyText>
                  <Box
                    as="span"
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      handleNavigate(link);
                    }}
                    color={theme.colors.blue}
                    cursor="pointer"
                    sx={{textDecoration: 'underline'}}
                  >
                    {displayLink}
                  </Box>
                </BodyText>
              )}
            </motion.div>
            <Flex align="center" gap="6px" mt="4px">
              <Flex align="center" justify="center">
                <Icon
                  iconName={isSms ? 'fi-rr-comment' : 'fi-rr-mobile-notch'}
                  style={{
                    fontSize: '14px',
                    width: '14px',
                    height: '14px',
                    color: '#8c8c8cff',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                />
              </Flex>
              <TextG12
                color="#9e9e9e"
                fontWeight="400"
                fontSize="12px"
                mt="0"
              >
                {formattedTimestamp}
              </TextG12>
            </Flex>
          </Flex>
          {isUnread && (
            <Flex align="center" alignSelf="center" ml="auto" pl={2}>
              <Box
                w="8px"
                h="8px"
                borderRadius="full"
                bg={theme.colors.blue}
              />
            </Flex>
          )}
        </Flex>
      </Box>

      <BottomModalSheet
        openModal={showMessageSheet}
        onModalClose={() => setShowMessageSheet(false)}
      >
        <Box px={4} pb={6}>
          <Flex direction="column" align="center" textAlign="center">
            <Box
              as="img"
              src={messageImage}
              alt="Message"
              borderRadius="full"
              h="64px"
              w="64px"
              objectFit="cover"
              border="1px solid rgba(0, 0, 0, 0.05)"
              bg="#f5f5f5"
              mb={4}
            />
            {displayTitle && (
              <TextA18 fontWeight="600" m={0} mb={2}>
                {displayTitle}
              </TextA18>
            )}
            <TextG14 lineHeight="24px" m={0} whiteSpace="pre-line">
              {body}
            </TextG14>
          </Flex>
        </Box>
      </BottomModalSheet>
    </Box>
  );
};

export default MessageCard;
